// UTM 參數追蹤工具

export interface UTMParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string; // Google Ads Click ID
  fbclid?: string; // Facebook Click ID
  referrer?: string;
  landing_page?: string;
  timestamp?: string;
}

/**
 * 從 URL 中提取 UTM 參數
 */
export const getUTMParams = (): UTMParams => {
  const params = new URLSearchParams(window.location.search);
  
  return {
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_term: params.get('utm_term') || undefined,
    utm_content: params.get('utm_content') || undefined,
    gclid: params.get('gclid') || undefined,
    fbclid: params.get('fbclid') || undefined,
    referrer: document.referrer || undefined,
    landing_page: window.location.href,
    timestamp: new Date().toISOString()
  };
};

/**
 * 儲存 UTM 參數到 localStorage
 */
export const saveUTMParams = (params: UTMParams): void => {
  try {
    // 儲存最新的 UTM 參數
    localStorage.setItem('utm_params', JSON.stringify(params));
    
    // 儲存首次訪問的 UTM 參數（如果不存在）
    if (!localStorage.getItem('first_utm_params')) {
      localStorage.setItem('first_utm_params', JSON.stringify(params));
    }
    
    // 儲存訪問歷史
    const history = getUTMHistory();
    history.push(params);
    // 只保留最近 10 次記錄
    const recentHistory = history.slice(-10);
    localStorage.setItem('utm_history', JSON.stringify(recentHistory));
  } catch (error) {
    console.error('儲存 UTM 參數失敗:', error);
  }
};

/**
 * 獲取儲存的 UTM 參數
 */
export const getSavedUTMParams = (): UTMParams | null => {
  try {
    const saved = localStorage.getItem('utm_params');
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error('讀取 UTM 參數失敗:', error);
    return null;
  }
};

/**
 * 獲取首次訪問的 UTM 參數
 */
export const getFirstUTMParams = (): UTMParams | null => {
  try {
    const saved = localStorage.getItem('first_utm_params');
    return saved ? JSON.parse(saved) : null;
  } catch (error) {
    console.error('讀取首次 UTM 參數失敗:', error);
    return null;
  }
};

/**
 * 獲取 UTM 訪問歷史
 */
export const getUTMHistory = (): UTMParams[] => {
  try {
    const history = localStorage.getItem('utm_history');
    return history ? JSON.parse(history) : [];
  } catch (error) {
    console.error('讀取 UTM 歷史失敗:', error);
    return [];
  }
};

/**
 * 清除 UTM 參數
 */
export const clearUTMParams = (): void => {
  try {
    localStorage.removeItem('utm_params');
    localStorage.removeItem('first_utm_params');
    localStorage.removeItem('utm_history');
  } catch (error) {
    console.error('清除 UTM 參數失敗:', error);
  }
};

/**
 * 檢查是否有任何 UTM 參數
 */
export const hasUTMParams = (params: UTMParams): boolean => {
  return !!(
    params.utm_source ||
    params.utm_medium ||
    params.utm_campaign ||
    params.utm_term ||
    params.utm_content ||
    params.gclid ||
    params.fbclid
  );
};

/**
 * 格式化 UTM 參數為字串
 */
export const formatUTMParams = (params: UTMParams): string => {
  const parts: string[] = [];
  
  if (params.utm_source) parts.push(`來源: ${params.utm_source}`);
  if (params.utm_medium) parts.push(`媒介: ${params.utm_medium}`);
  if (params.utm_campaign) parts.push(`活動: ${params.utm_campaign}`);
  if (params.utm_term) parts.push(`關鍵字: ${params.utm_term}`);
  if (params.utm_content) parts.push(`內容: ${params.utm_content}`);
  if (params.gclid) parts.push(`Google Ads`);
  if (params.fbclid) parts.push(`Facebook Ads`);
  
  return parts.join(' | ') || '直接訪問';
};

/**
 * Google Ads 轉換追蹤
 * 在 GTM 後台設定後，使用此函數觸發轉換事件
 */
export const trackConversion = (conversionLabel: string, conversionValue?: number) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'conversion',
      conversionLabel,
      conversionValue: conversionValue || 0,
      currency: 'TWD'
    });
    
    console.log('Conversion tracked:', {
      label: conversionLabel,
      value: conversionValue
    });
  }
};

/**
 * 表單提交轉換追蹤
 */
export const trackFormSubmission = (formType: 'quote' | 'contact', formData?: Record<string, unknown>) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'form_submission',
      formType,
      formData: formData || {},
      timestamp: new Date().toISOString()
    });
    
    console.log('Form submission tracked:', formType);
  }
};

/**
 * 電話點擊轉換追蹤
 */
export const trackPhoneClick = (phoneNumber: string) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'phone_click',
      phoneNumber,
      timestamp: new Date().toISOString()
    });
    
    console.log('Phone click tracked:', phoneNumber);
  }
};

/**
 * LINE 按鈕點擊轉換追蹤
 */
export const trackLineClick = (lineId: string) => {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push({
      event: 'line_click',
      lineId,
      timestamp: new Date().toISOString()
    });
    
    console.log('LINE click tracked:', lineId);
  }
};
